import css from '@styled-system/css';
import React from 'react';
import styled from 'styled-components';
import { border, color, fontSize, layout, space, typography, variant } from 'styled-system';
import PropTypes from 'prop-types';
import { Text } from 'atoms/Text';
import { Box } from 'atoms/Box';

const SCALE = 'scale(1.004)';
const SECONDARY = 'secondary.0';

const variants = {
	primary: {
		color: 'white',
		px: 2,
		py: 3,
		backgroundColor: 'primary',
		transition: 'all 0.3s',
		border: '1px solid',
		borderColor: 'primary',
		fontWeight: 600,
		letterSpacing: 0.4,
		borderRadius: 6,
		h3: {
			textTransform: 'uppercase',
		},
		'&:hover': {
			transform: SCALE,
			borderColor: 'primary.400',
			backgroundColor: 'primary.400',
		},
		'&:focus': {
			outline: 0,
		},
		'&:disabled': {
			backgroundColor: 'white.300',
			borderColor: 'white.300',
			color: 'white.0',
		},
	},
	outlinePrimary: {
		color: 'primary',
		px: 2,
		py: 3,
		backgroundColor: 'white',
		transition: 'all 0.3s',
		border: '1px solid',
		borderColor: 'primary',
		fontWeight: 600,
		letterSpacing: 0.4,
		borderRadius: 6,
		h3: {
			textTransform: 'uppercase',
		},
		'&:hover': {
			transform: SCALE,
			color: 'white',
			backgroundColor: 'primary',
		},
		'&:focus': {
			outline: 0,
		},
		'&:disabled': {
			backgroundColor: 'white.300',
			borderColor: 'white.300',
			color: 'white.0',
		},
	},
	secondary: {
		color: 'white.0',
		px: 6,
		py: 2,
		backgroundColor: 'secondary.50',
		borderRadius: 4,
		border: '1px solid',
		borderColor: SECONDARY,
		transition: 'all 0.3s',
		h3: {
			textTransform: 'uppercase',
		},
		'&:hover': {
			transform: SCALE,
			color: 'white.0',
			backgroundColor: 'secondary.100',
			borderColor: 'secondary.100',
		},
		'&:active': {
			backgroundColor: 'secondary.400',
			borderColor: 'secondary.400',
		},
		'&:disabled': {
			backgroundColor: 'white.300',
			borderColor: 'white.300',
			color: 'white.0',
		},
	},
	tertiary: {
		color: 'white.0',
		backgroundColor: 'transparent',
		border: '1px solid',
		borderColor: 'primary.500',
		transition: 'all 0.3s',
		borderRadius: 4,
		h3: {
			textTransform: 'uppercase',
		},
		'&:hover': {
			transform: SCALE,
			color: 'black.0',
			backgroundColor: 'white.0',
		},
	},
	danger: {
		color: 'red',
		px: 1,
		py: 1,
		transition: 'all 0.3s',
		backgroundColor: 'transparent',
		borderRadius: 0,
		border: 0,
		cursor: 'pointer',
	},
	transparent: {
		color: SECONDARY,
		px: 6,
		py: 3,
		transition: 'all 0.3s',
		border: 0,
		backgroundColor: 'transparent',
		borderRadius: 4,
		'&:disabled': {
			borderColor: 'white.300',
			color: 'white.300',
		},
	},
	disabled: {
		color: 'white.0',
		backgroundColor: SECONDARY,
		borderRadius: 4,
		border: '1px solid',
		borderColor: SECONDARY,
		transition: 'all 0.3s',
		h3: {
			textTransform: 'uppercase',
		},
		'&:hover': {
			transform: SCALE,
			color: 'white.0',
			backgroundColor: SECONDARY,
		},
	},
};

const Container = styled(Box)`
	cursor: pointer;
	position: relative;
	white-space: nowrap;
	${variant({
		variants,
	})}
	${({ disabled }) =>
		disabled &&
		css({
			// backgroundColor: 'gray.200',
			boxShadow: 'none',
			cursor: 'not-allowed',
		})}
	${({ block }) =>
		block &&
		css({
			width: '100%',
		})}
  	${({ whiteSpace }) =>
		whiteSpace &&
		css({
			whiteSpace,
		})}
	${layout}
	${color}
	${typography}
	${space}
	${fontSize}
	${border}
	&:disabled {
		opacity: 0.2;
	}
`;

export function Button({
	disabled,
	loading,
	children,
	variant: v = 'primary',
	fontFamily,
	...rest
}) {
	return (
		<Container
			variant={v}
			as="button"
			{...rest}
			disabled={disabled || loading}
			overflow="hidden"
		>
			<Text opacity={loading ? 0 : 1} fontFamily={fontFamily} id={rest.id}>
				{children}
			</Text>
		</Container>
	);
}

Button.propTypes = {
	disabled: PropTypes.bool,
	loading: PropTypes.bool,
	variant: PropTypes.string,
	fontFamily: PropTypes.string,
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
